/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout, Button, Form, Input, Radio, Switch, Modal } from 'antd'
import { CustomerServiceOutlined, UndoOutlined } from '@ant-design/icons'
import AntdProvider from '@/components/AntdProvider'
import dayjs from 'dayjs'
import { v4 as uuid } from 'uuid'
import { getOSSToken, setLoading, uploadToOSS } from '../../actions/main'
import FileSaver from 'file-saver'
import { toPng } from 'html-to-image'
import classnames from 'classnames'
import CONFIG from '../../constants/config'
import sensors from '../../utils/sensors'
import { dataURLtoBlob } from '../../utils/imageUtils'
import parseQueryString from '../../utils/parseQueryString'

import layout1 from '../../images/jigsaw-examples/layout_1.png'
import layout2 from '../../images/jigsaw-examples/layout_2.png'
import layout3 from '../../images/jigsaw-examples/layout_3.png'
import aiContentImg from '../../images/jigsaw-examples/ai_content.png'
import tableIconImg from '../../images/jigsaw-examples/table_icon.png'
import {
  clearPhotoPickerPhotos,
  setBackFromPickerPage
} from '../../actions/photoPicker'
import ContactUS from '../components/ContactUS'
import Result from './Result'
import UseHistory from './UseHistory'
import { capturePromiseException } from '../../utils/sentryUtils'
import { themeColors } from './lib'
import WorkProgress from './WorkProgress'
import ThemeColor from './ThemeColor'
import TableSetting from './TableSetting'
import PhotoReportEntry from '../aiClerk/components/PhotoReportEntry'
import { useAppSelector, useAppDispatch } from '../../app/hooks'

import PageHeader from '@/components/PageHeader'
import type { JigsawPhoto, JigsawData, TableConfig } from './types'
const { Content } = Layout
const FormItem = Form.Item
const RadioGroup = Radio.Group
const TextArea = Input.TextArea

function Jigsaw() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Redux 状态
  const groupId = useAppSelector((state) => state.group.groupId)
  const group = useAppSelector((state) => state.group)
  const user = useAppSelector((state) => state.main.user)
  const exportCheckedPhotos = useAppSelector(
    (state) => state.photo.checkedPhotos
  )
  const exportPhotos = useAppSelector((state) => state.photo.photos)
  const exportCheckAll = useAppSelector((state) => state.photo.checkAll)
  const exportUnCheckedPhotos = useAppSelector(
    (state) => state.photo.unCheckedPhotos
  )
  const selectedPhotos = useAppSelector((state) => state.photoPicker.photos)
  const backFromPickerPage = useAppSelector(
    (state) => state.photoPicker.backFromPickerPage
  )

  // 本地状态
  const [photos, setPhotos] = useState<JigsawPhoto[]>([])
  const [jigsawData, setJigsawData] = useState<JigsawData>({
    title: '今日工作汇报',
    date: dayjs().format('YYYY年MM月DD日'),
    reportor: user?.nickname,
    mobile: user?.mobile,
    company: group.groupName.slice(0, 12),
    copilotSwitch: false,
    avatarSwitch: true,
    mainBody: '正文：',
    type: 2,
    progress: 2,
    progressSwitch: true,
    themeColor: themeColors[1].color,
    tableConfig: {
      rows: 3,
      cols: 3,
      cellAlign: 'left',
      tableSwitch: true,
      data: [
        ['', '', ''],
        ['', '', ''],
        ['', '', '']
      ]
    }
  })

  // refs
  const resultRef = useRef<HTMLDivElement>(null)
  const fromRef = useRef(parseQueryString().from)

  // 保存历史记录
  const saveHistory = useCallback(() => {
    const {
      title,
      reportor,
      avatarSwitch,
      mobile,
      company,
      mainBody,
      progressSwitch,
      themeColor,
      tableConfig
    } = jigsawData
    let isOpen = false
    try {
      if (JSON.parse(localStorage.getItem('jigsaw_isUseHistory') || 'false')) {
        localStorage.setItem(
          'jigsaw_history',
          JSON.stringify({
            title,
            reportor,
            avatarSwitch,
            mobile,
            company,
            mainBody,
            progressSwitch,
            themeColor,
            tableConfig
          })
        )
        isOpen = true
      }
    } catch (error) {}
    return isOpen
  }, [jigsawData])

  // 使用历史记录
  const useHistoryData = useCallback(() => {
    try {
      if (JSON.parse(localStorage.getItem('jigsaw_isUseHistory') || 'false')) {
        const jigsawHistory = JSON.parse(
          localStorage.getItem('jigsaw_history') || 'null'
        )
        if (jigsawHistory) {
          setJigsawData((prev) => ({
            ...prev,
            ...jigsawHistory
          }))
        }
      }
    } catch (error) {
      console.log('use history error', error)
    }
  }, [])

  // 使用缓存
  const useCache = useCallback(() => {
    try {
      const cachedData = JSON.parse(
        localStorage.getItem('jigsaw_data_cache') || 'null'
      )
      if (cachedData) {
        setJigsawData(cachedData)
      }
    } catch (error) {
      console.log('use cache error', error)
    }
  }, [])

  // 获取选中的照片
  const getCheckPhotos = useCallback(() => {
    let newPhotos: JigsawPhoto[] = []
    const checkedPhotosValues = Object.values(exportCheckedPhotos)
    if (fromRef.current === 'export') {
      if (checkedPhotosValues.length) {
        newPhotos = checkedPhotosValues.filter(
          (photo) => photo.mediaType === 0
        ) as JigsawPhoto[]
      } else if (exportPhotos.length && exportCheckAll) {
        newPhotos = exportPhotos.filter(
          (exportPhoto) =>
            !(exportPhoto.seq in exportUnCheckedPhotos) &&
            exportPhoto.mediaType === 0
        ) as JigsawPhoto[]
      } else if (selectedPhotos.length) {
        newPhotos = selectedPhotos.filter(
          (photo) => photo.mediaType === 0
        ) as JigsawPhoto[]
      }
    } else if (fromRef.current === 'menu' && selectedPhotos.length) {
      newPhotos = selectedPhotos.filter(
        (photo) => photo.mediaType === 0
      ) as JigsawPhoto[]
    }

    setPhotos(newPhotos)

    if (backFromPickerPage) {
      // 从选择照片页面回到拼图页面
      sensors.track('web_workgroup_jigsaw_picked_entry', {
        from: fromRef.current
      })
      useCache()
    } else {
      // 从入口进入拼图页面
      sensors.track('web_workgroup_jigsaw_entry', {
        from: fromRef.current
      })
      useHistoryData()
    }
    dispatch(setBackFromPickerPage(false))
  }, [
    exportCheckedPhotos,
    exportPhotos,
    exportCheckAll,
    exportUnCheckedPhotos,
    selectedPhotos,
    backFromPickerPage,
    dispatch,
    useCache,
    useHistoryData
  ])

  // 初始化（仅组件挂载时执行一次）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getCheckPhotos()

    const mainBodyInputEle = document.querySelector(
      '#mainBodyInput'
    ) as HTMLInputElement | null
    if (mainBodyInputEle) {
      mainBodyInputEle.setSelectionRange(0, mainBodyInputEle.value.length)
    }

    if (!__DEBUG__) {
      window.onbeforeunload = (e) => {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    return () => {
      dispatch(clearPhotoPickerPhotos())
    }
  }, [])

  // 处理选择照片
  const handlePickPhotos = useCallback(() => {
    setPhotos([])
    dispatch(clearPhotoPickerPhotos())
    localStorage.setItem('jigsaw_data_cache', JSON.stringify(jigsawData))
    navigate(
      encodeURI(
        `/photo/picker?from=jigsaw&pageTitle=选择拼图照片&mediaType=0&max=${CONFIG.jigsawMaxNum}`
      )
    )
  }, [dispatch, jigsawData, navigate])

  // 处理重新选择照片
  const handleRepickPhotosClick = useCallback(() => {
    sensors.track('web_workgroup_jigsaw_click', {
      clickItem: 'repick',
      num: photos.length
    })
    handlePickPhotos()
  }, [photos.length, handlePickPhotos])

  // 上报照片水印信息
  const reportPhotoWatermark = useCallback(() => {
    photos.forEach((photo) => {
      sensors.track('web_workgroup_jigsaw_photo_track', {
        fileName: photo.file,
        sourceType: photo.sourceType,
        photoDateTime: dayjs(photo.time).format(CONFIG.dateFormat),
        hasWatermark: photo.hasWatermark,
        baseID: photo.watermarkBaseID
      })
    })
  }, [photos])

  // 处理进度变化
  const handleProgressChange = useCallback((value) => {
    setJigsawData((prev) => ({ ...prev, progress: value }))
  }, [])

  // 处理保存
  const handleSave = useCallback(() => {
    const {
      title,
      date,
      reportor,
      mobile,
      company,
      copilotSwitch,
      avatarSwitch,
      progress,
      progressSwitch,
      mainBody,
      type,
      themeColor,
      tableConfig
    } = jigsawData
    const colorName = themeColors.find(
      (item) => item.color === themeColor
    )?.name
    const photoNum = photos.length
    const useHistoryOpened = saveHistory()
    dispatch(setLoading(true))
    reportPhotoWatermark()
    if (!resultRef.current) return
    toPng(resultRef.current, {
      pixelRatio: 3,
      filter: (node) => {
        return !['add-photos-area'].some((className) =>
          node.classList?.contains(className)
        )
      }
    }).then((dataUrl) => {
      FileSaver.saveAs(dataUrl, '拼图.jpeg')
      sensors.track('web_workgroup_jigsaw_click_save', {
        from: fromRef.current,
        photoNum: photos.length,
        title,
        mainBody,
        type,
        reportDate: date,
        reportor,
        reportMobile: mobile,
        companyName: company,
        copilotSwitch,
        avatarSwitch,
        workProgress: progress,
        workProgressSwitch: progressSwitch,
        useHistoryOpened,
        color: colorName,
        tableSwitch: tableConfig.tableSwitch,
        rowNum: tableConfig.rows,
        colNum: tableConfig.cols
      })
      dispatch(setLoading(false))

      let filename = uuid() + '.png'
      const bucketName = 'net-cloud'
      const blob = dataURLtoBlob(dataUrl)
      dispatch(getOSSToken()).then((ossToken) => {
        const bucketConfig = ossToken[bucketName]
        uploadToOSS({
          key: `web/jigsaw_result/${filename}`,
          OSSAccessKeyId: bucketConfig.accessid,
          policy: bucketConfig.policy,
          Signature: bucketConfig.signature,
          file: new File([blob], filename, {
            type: 'image/png',
            lastModified: Date.now()
          })
        }).catch(capturePromiseException)
      })
      sensors.track('web_workgroup_jigsaw_save_result', {
        fileName: filename,
        photoNum,
        title,
        mainBody,
        type,
        reportDate: date,
        reportor,
        reportMobile: mobile,
        companyName: company,
        copilotSwitch,
        avatarSwitch,
        workProgress: progress,
        workProgressSwitch: progressSwitch,
        useHistoryOpened,
        color: colorName,
        from: fromRef.current,
        groupID: groupId,
        groupName: group.groupName,
        isVip: group.isVip,
        vipKind: group.vipInfo?.vip_kind,
        industryCode: group.industryCode,
        userId: user?.user_id,
        username: user?.nickname,
        phoneNumber: user?.mobile,
        tableSwitch: tableConfig.tableSwitch,
        rowNum: tableConfig.rows,
        colNum: tableConfig.cols
      })
    })
  }, [
    jigsawData,
    photos,
    dispatch,
    saveHistory,
    reportPhotoWatermark,
    groupId,
    group,
    user
  ])

  // 处理保存点击
  const handleSaveClick = useCallback(() => {
    if (photos.length === 0) {
      Modal.confirm({
        title: '未选择拼图照片，是否继续保存？',
        okText: '去选照片',
        cancelText: '保存',
        onOk: () => {
          sensors.track('web_workgroup_jigsaw_click', {
            clickItem: 'save_modal_pick_photos'
          })
          handlePickPhotos()
        },
        onCancel: () => {
          sensors.track('web_workgroup_jigsaw_click', {
            clickItem: 'save_modal_download'
          })
          handleSave()
        }
      })
    } else {
      handleSave()
    }
  }, [photos.length, handlePickPhotos, handleSave])

  const {
    title,
    date,
    reportor,
    mobile,
    company,
    type,
    mainBody,
    copilotSwitch,
    avatarSwitch,
    progress,
    progressSwitch,
    themeColor,
    tableConfig
  } = jigsawData

  return (
    <AntdProvider>
      <Layout className="Jigsaw">
        <PageHeader
          className="Header"
          ghost={true}
          title={
            <div className="title">
              <span>照片拼图</span>
              {/* AI 填表员 · 拼图汇报入口(可关闭,关闭后不再展示) */}
              <PhotoReportEntry />
              <Button
                key="1"
                type="primary"
                className="save-btn"
                onClick={handleSaveClick}
              >
                保存拼图汇报
              </Button>
            </div>
          }
        />
        <Content className="content">
          <div>
            <div className="control-panel">
              <Form
                layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
              >
                <FormItem label="主题色">
                  <ThemeColor
                    curColor={themeColor}
                    onChange={(color) =>
                      setJigsawData((prev) => ({ ...prev, themeColor: color }))
                    }
                  />
                </FormItem>
                <FormItem label="标题" help="标题最多18个汉字，不支持换行。">
                  <Input
                    placeholder="请输入标题"
                    value={title}
                    onChange={(e) =>
                      setJigsawData((prev) => ({
                        ...prev,
                        title: e.target.value.trim()
                      }))
                    }
                    maxLength={18}
                  />
                </FormItem>
                <FormItem label="日期">
                  <Input
                    placeholder="请输入日期"
                    value={date}
                    onChange={(e) =>
                      setJigsawData((prev) => ({
                        ...prev,
                        date: e.target.value.trim()
                      }))
                    }
                    maxLength={12}
                    allowClear
                  />
                </FormItem>
                <FormItem label="汇报人">
                  <Input
                    placeholder="请输入汇报人"
                    value={reportor}
                    onChange={(e) =>
                      setJigsawData((prev) => ({
                        ...prev,
                        reportor: e.target.value.trim()
                      }))
                    }
                    maxLength={10}
                    allowClear
                  />
                  开启头像
                  <Switch
                    size="small"
                    disabled={reportor ? false : true}
                    style={{ marginLeft: 5 }}
                    checked={avatarSwitch}
                    onChange={(checked) => {
                      sensors.track('web_workgroup_jigsaw_click', {
                        clickItem: 'avatarSwitch'
                      })
                      setJigsawData((prev) => ({
                        ...prev,
                        avatarSwitch: checked
                      }))
                    }}
                  />
                </FormItem>
                <FormItem label="联系方式">
                  <Input
                    placeholder="请输入联系方式"
                    value={mobile}
                    onChange={(e) =>
                      setJigsawData((prev) => ({
                        ...prev,
                        mobile: e.target.value.trim()
                      }))
                    }
                    maxLength={11}
                    allowClear
                  />
                </FormItem>
                <FormItem label="部门/公司">
                  <Input
                    placeholder="请输入部门/公司"
                    value={company}
                    onChange={(e) =>
                      setJigsawData((prev) => ({
                        ...prev,
                        company: e.target.value.trim()
                      }))
                    }
                    maxLength={12}
                    allowClear
                  />
                </FormItem>
                <FormItem label="开启AI助写">
                  <img
                    style={{
                      width: 90,
                      marginRight: 10,
                      verticalAlign: 'middle'
                    }}
                    src={aiContentImg}
                    onClick={() =>
                      sensors.track('web_workgroup_jigsaw_click', {
                        clickItem: 'ai_content'
                      })
                    }
                  />
                  <Switch
                    disabled={photos.length === 0}
                    checked={copilotSwitch}
                    onChange={(checked) => {
                      sensors.track('web_workgroup_jigsaw_click', {
                        clickItem: 'copilotSwitch',
                        open: checked
                      })
                      setJigsawData((prev) => ({
                        ...prev,
                        copilotSwitch: checked
                      }))
                    }}
                  />
                </FormItem>
                <FormItem label="工作进度">
                  <WorkProgress
                    progress={progress}
                    disabled={!progressSwitch}
                    onChange={handleProgressChange}
                  />
                  开启工作进度
                  <Switch
                    size="small"
                    style={{ marginLeft: 5 }}
                    checked={progressSwitch}
                    onChange={(checked) => {
                      sensors.track('web_workgroup_jigsaw_click', {
                        clickItem: 'progressSwitch'
                      })
                      setJigsawData((prev) => ({
                        ...prev,
                        progressSwitch: checked
                      }))
                    }}
                  />
                </FormItem>
                <FormItem
                  label={
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <img
                        style={{
                          width: '16px',
                          marginRight: '2px',
                          marginBottom: '2px'
                        }}
                        src={tableIconImg}
                      />
                      表格
                    </span>
                  }
                >
                  <TableSetting
                    jigsawData={jigsawData}
                    onChange={(newTableConfig) =>
                      setJigsawData((prev) => ({
                        ...prev,
                        tableConfig: newTableConfig
                      }))
                    }
                  />
                </FormItem>
                <FormItem label="汇报正文">
                  <TextArea
                    id="mainBodyInput"
                    placeholder="今日工作详细内容"
                    value={mainBody}
                    onChange={(e) => {
                      setJigsawData((prev) => ({
                        ...prev,
                        mainBody: e.target.value
                      }))
                    }}
                    autoFocus={true}
                    maxLength={800}
                    rows={8}
                  />
                  <UseHistory />
                </FormItem>
                <FormItem
                  label="图片布局"
                  labelCol={{ span: 4 }}
                  wrapperCol={{ span: 20, offset: 3 }}
                >
                  <RadioGroup
                    value={type.toString()}
                    onChange={(e) => {
                      sensors.track('web_workgroup_jigsaw_click', {
                        clickItem: 'layout',
                        from: fromRef.current,
                        type: parseInt(e.target.value)
                      })
                      setJigsawData((prev) => ({
                        ...prev,
                        type: parseInt(e.target.value)
                      }))
                    }}
                  >
                    <div className="layout-items-wrapper">
                      {['1', '2', '3'].map((layout) => (
                        <div
                          key={layout}
                          className={classnames('layout-item', {
                            'layout-item-selected': type.toString() === layout
                          })}
                        >
                          <img
                            src={
                              layout === '1'
                                ? layout1
                                : layout === '2'
                                  ? layout2
                                  : layout3
                            }
                            alt=""
                            onClick={() => {
                              sensors.track('web_workgroup_jigsaw_click', {
                                clickItem: 'layout',
                                from: fromRef.current,
                                type: parseInt(layout)
                              })
                              setJigsawData((prev) => ({
                                ...prev,
                                type: parseInt(layout)
                              }))
                            }}
                          />
                          <Radio value={layout}>
                            {layout === '1'
                              ? '单列'
                              : layout === '2'
                                ? '双列'
                                : '三列'}
                          </Radio>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </FormItem>
              </Form>
            </div>
          </div>
          <div className="result-wrapper">
            <Result
              ref={resultRef}
              photos={photos}
              jigsawData={jigsawData}
              onChange={(newJigsawData) => setJigsawData(newJigsawData)}
            />
          </div>
          <div className="right-buttons">
            <ContactUS fromPage="拼图汇报">
              <Button
                type="primary"
                className="contact-us-btn"
                onClick={() => {
                  sensors.track('web_workgroup_jigsaw_click', {
                    clickItem: 'kefu'
                  })
                }}
              >
                <CustomerServiceOutlined />
                建议反馈
              </Button>
            </ContactUS>
            <Button type="default" onClick={handleRepickPhotosClick}>
              <UndoOutlined />
              重新选图
            </Button>
          </div>
        </Content>
      </Layout>
    </AntdProvider>
  )
}

export default Jigsaw
